import { createFileRoute, Link } from "@tanstack/react-router";

import { Footer, Nav } from "../components/lidoo/chrome";
import { listPosts } from "../lib/api/lidoo.functions";
import { useI18n } from "../lib/i18n";
import { hreflangLinks } from "../lib/seo";

export const Route = createFileRoute("/guides/")({
  loader: async () => await listPosts({ data: { lang: "en" } }),
  head: () => ({
    meta: [
      { title: "Beach club guides | sunbedly" },
      {
        name: "description",
        content:
          "Guides to the best beach clubs on each coast we map, built from real published prices rather than sponsored placements.",
      },
    ],
    links: hreflangLinks("/guides"),
  }),
  component: () => <GuidesIndex posts={Route.useLoaderData().posts} />,
});

export type PostSummary = {
  slug: string;
  title: string;
  excerpt: string;
  region_key: string | null;
};

// Presentational, so the /nl/guides, /it/guides ... routes can render the same
// page from their own loader without duplicating any markup.
export function GuidesIndex({ posts }: { posts: PostSummary[] }) {
  const { t } = useI18n();

  return (
    <div className="min-h-dvh bg-[#F2EFE9]">
      <Nav />
      <main className="mx-auto max-w-6xl px-5 pb-24">
        <div className="border-b border-[#1C2430]/15 pb-8 pt-12">
          <p className="li-mono text-[11px] uppercase tracking-widest text-[#2C4FD6]">
            {t("guides_eyebrow")}
          </p>
          <h1 className="li-serif mt-3 max-w-[18ch] text-4xl text-[#1C2430] md:text-6xl">
            {t("guides_title")}
          </h1>
          <p className="mt-4 max-w-[58ch] text-lg leading-relaxed text-[#5B6472]">
            {t("guides_sub")}
          </p>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {posts.map((p) => (
            <Link
              key={p.slug}
              to="/guides/$slug"
              params={{ slug: p.slug }}
              className="group flex flex-col rounded-2xl border border-[#1C2430]/10 bg-white/70 p-6 transition-all hover:-translate-y-0.5 hover:border-[#1C2430]/20 hover:shadow-[0_18px_40px_-24px_rgba(28,36,48,0.45)]"
            >
              <p className="li-mono text-[11px] uppercase tracking-widest text-[#2C4FD6]">
                {p.region_key?.replace("-", " ")}
              </p>
              <h2 className="li-serif mt-3 text-2xl leading-tight text-[#1C2430]">{p.title}</h2>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-[#5B6472]">{p.excerpt}</p>
              <span className="li-mono mt-5 text-[12px] text-[#1C2430] underline decoration-[#2C4FD6] underline-offset-4">
                {t("guides_read")} →
              </span>
            </Link>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
